import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Target, Plus, Trophy, X, Check, Flame } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const GOAL_TYPE_LABELS = {
  weekly_pages: "Pages / Week",
  weekly_papers: "Papers / Week",
  monthly_pages: "Pages / Month",
  monthly_papers: "Papers / Month",
};

const GOAL_TYPE_ICONS = {
  weekly_pages: "pages",
  weekly_papers: "papers",
  monthly_pages: "pages",
  monthly_papers: "papers",
};

export default function GoalsWidget({ api }) {
  const [goals, setGoals] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [goalType, setGoalType] = useState("weekly_pages");
  const [target, setTarget] = useState(20);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchGoals = async () => {
    try {
      const res = await axios.get(`${api}/goals`, { withCredentials: true });
      setGoals(res.data);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchGoals();
  }, [api]);

  const handleCreate = async () => {
    if (target < 1) {
      toast.error("Target must be at least 1");
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${api}/goals`, { goal_type: goalType, target }, { withCredentials: true });
      toast.success("Goal created! Let's crush it.");
      setShowCreate(false);
      fetchGoals();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create goal");
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (goalId) => {
    try {
      await axios.delete(`${api}/goals/${goalId}`, { withCredentials: true });
      setGoals((prev) => prev.filter((g) => g.id !== goalId));
      toast.success("Goal removed");
    } catch {
      toast.error("Failed to remove goal");
    }
  };

  if (loading) return null;

  return (
    <div data-testid="goals-widget">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight dark:text-white">
          Reading Goals
        </h2>
        <button
          onClick={() => setShowCreate(true)}
          className="neo-btn neo-btn-primary flex items-center gap-2 text-sm"
          data-testid="create-goal-btn"
        >
          <Plus className="w-4 h-4" />
          <span className="hidden sm:inline">New Goal</span>
        </button>
      </div>

      {goals.length === 0 ? (
        <div className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] text-center py-8" data-testid="no-goals">
          <Target className="w-12 h-12 text-neo-blue mx-auto mb-3" />
          <p className="font-heading font-bold text-lg dark:text-white">Set Your First Goal</p>
          <p className="text-sm text-neo-text-secondary dark:text-gray-400 mt-1">
            Challenge yourself with weekly or monthly reading targets
          </p>
          <button
            onClick={() => setShowCreate(true)}
            className="neo-btn neo-btn-primary mt-4"
            data-testid="set-first-goal-btn"
          >
            Set a Goal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {goals.map((goal) => {
            const progress = goal.target > 0 ? Math.min((goal.current / goal.target) * 100, 100) : 0;
            const isComplete = goal.completed;
            return (
              <div
                key={goal.id}
                className={`neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[4px_4px_0px_0px_#444] relative ${
                  isComplete ? "border-neo-green" : ""
                }`}
                data-testid={`goal-${goal.id}`}
              >
                {isComplete && (
                  <div className="absolute -top-2 -right-2 w-8 h-8 rounded-full bg-neo-green border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] flex items-center justify-center">
                    <Check className="w-4 h-4 text-neo-text" />
                  </div>
                )}
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-10 h-10 rounded-lg border-2 border-neo-border dark:border-[#444] shadow-[2px_2px_0px_0px_#1A1A1A] dark:shadow-[2px_2px_0px_0px_#444] flex items-center justify-center ${
                      isComplete ? "bg-neo-green" : "bg-neo-blue"
                    }`}>
                      <Target className="w-5 h-5 text-neo-text" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-sm dark:text-white">{goal.label}</p>
                      <p className="text-xs text-neo-text-secondary dark:text-gray-400">
                        {GOAL_TYPE_LABELS[goal.goal_type] || goal.goal_type}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleDelete(goal.id)}
                    className="text-neo-text-secondary dark:text-gray-400 hover:text-neo-red p-1"
                    data-testid={`delete-goal-${goal.id}`}
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-neo-text-secondary dark:text-gray-400">Progress</span>
                    <span className="font-bold dark:text-white">
                      {goal.current}/{goal.target} {GOAL_TYPE_ICONS[goal.goal_type] || ""}
                    </span>
                  </div>
                  <div className="neo-progress">
                    <div
                      className="neo-progress-fill"
                      style={{
                        width: `${progress}%`,
                        background: isComplete ? "#4ADE80" : "#FFD836",
                      }}
                    />
                  </div>
                  {isComplete && (
                    <div className="flex items-center gap-1 text-sm text-neo-green">
                      <Trophy className="w-4 h-4" />
                      <span className="font-bold">+200 XP Bonus!</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create Goal Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="neo-card dark:bg-[#1E1E1E] dark:border-[#444] dark:shadow-[6px_6px_0px_0px_#444] border-[3px] border-neo-border shadow-[6px_6px_0px_0px_#1A1A1A] max-w-md" data-testid="create-goal-modal">
          <DialogHeader>
            <DialogTitle className="font-heading font-extrabold text-2xl flex items-center gap-2 dark:text-white">
              <Target className="w-6 h-6 text-neo-blue" />
              New Reading Goal
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-2">
                Goal Type
              </label>
              <Select value={goalType} onValueChange={setGoalType}>
                <SelectTrigger className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg" data-testid="goal-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly_pages" data-testid="goal-type-weekly-pages">Pages per Week</SelectItem>
                  <SelectItem value="weekly_papers" data-testid="goal-type-weekly-papers">Papers per Week</SelectItem>
                  <SelectItem value="monthly_pages" data-testid="goal-type-monthly-pages">Pages per Month</SelectItem>
                  <SelectItem value="monthly_papers" data-testid="goal-type-monthly-papers">Papers per Month</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] font-semibold text-neo-text-secondary dark:text-gray-400 block mb-2">
                Target
              </label>
              <Input
                type="number"
                min={1}
                value={target}
                onChange={(e) => setTarget(parseInt(e.target.value) || 1)}
                className="border-2 border-neo-border dark:border-[#444] dark:bg-[#2A2A2A] dark:text-white rounded-lg py-3 focus:ring-0 focus:border-[3px] focus:border-neo-yellow"
                data-testid="goal-target-input"
              />
              <p className="text-xs text-neo-text-secondary dark:text-gray-400 mt-1">
                {goalType.includes("pages") ? `Read ${target} pages` : `Complete ${target} papers`}{" "}
                {goalType.includes("weekly") ? "this week" : "this month"}
              </p>
            </div>
            <div className="neo-card dark:bg-[#2A2A2A] dark:border-[#555] bg-neo-yellow/10 border-2 border-neo-yellow p-3">
              <p className="text-sm font-bold dark:text-white flex items-center gap-1">
                <Flame className="w-4 h-4 text-neo-red" />
                +200 XP bonus when you hit your goal!
              </p>
            </div>
            <button
              onClick={handleCreate}
              disabled={creating}
              className={`neo-btn w-full py-3 ${creating ? "bg-gray-200 text-gray-500 border-gray-300 shadow-none cursor-not-allowed" : "neo-btn-success"}`}
              data-testid="confirm-create-goal-btn"
            >
              {creating ? "Creating..." : "Set Goal"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
